// Bündelung der Signalgraph-Knoten (Issue: Edges/Grouping/Thumbnail).
//
// Die gestempelten Regelmuster-Knoten tragen IDs der Form `<symbol>__<muster>__<rolle>`
// (z. B. `geblaese__air-set__sp`) und – seit der Stamp-Erweiterung – ein explizites `group`-Feld
// (id/label/function). Diese Funktionen leiten daraus die dezenten Gruppen ab, in die die
// Signalgraph-Ansicht die Knoten optisch (nach Herkunft) und logisch (nach Funktion) rahmt.
//
// Robust und rückwärtskompatibel: liegt kein `group`-Feld vor (ältere Graphen), wird die Gruppe
// aus dem `<symbol>`-Präfix der Knoten-ID abgeleitet. Knoten ohne ableitbares Präfix (frei
// gesetzte Einzelknoten) bleiben ungruppiert.

import type { GraphNode } from '../api/types';

// Eine abgeleitete Gruppe: die Knoten-IDs plus die Beschriftungen (optische Herkunft + Funktion).
export interface DerivedGroup {
  id: string; // stabiler Gruppen-Schlüssel (Symbol-Präfix)
  label: string; // Anzeigename (optische Herkunft), z. B. „Gebläse"
  function?: string; // funktionale Bezeichnung (logische Gruppierung), soweit ableitbar
  nodeIds: string[]; // die zu dieser Gruppe gehörenden Knoten-IDs
}

// Lesbarer Fallback-Name aus einem Slug/einer ID (Trenner → Leerzeichen, Wortanfänge groß).
function titleize(value: string): string {
  const words = value.split(/[_\-\s]+/).filter(Boolean);
  if (words.length === 0) return value;
  return words.map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

// Präfix (= erster `__`-Abschnitt) einer gestempelten Knoten-ID, sonst null.
function symbolPrefix(nodeId: string): string | null {
  const idx = nodeId.indexOf('__');
  if (idx <= 0) return null;
  return nodeId.slice(0, idx);
}

// Bestimmt Schlüssel + Beschriftungen der Gruppe eines Knotens. Bevorzugt das explizite
// `group`-Feld; fällt sonst auf die aus der Knoten-ID abgeleitete Herkunft zurück.
function groupOf(node: GraphNode): { id: string; label: string; function?: string } | null {
  const g = node.group;
  if (g && typeof g.id === 'string' && g.id) {
    return {
      id: g.id,
      label: g.label && g.label.trim() ? g.label : titleize(g.id),
      function: g.function && g.function.trim() ? g.function : undefined,
    };
  }
  const prefix = symbolPrefix(node.id);
  if (!prefix) return null;
  return { id: prefix, label: titleize(prefix) };
}

// Leitet aus den Graph-Knoten die Bündelungsgruppen ab (stabile Reihenfolge des ersten Auftretens).
// Nur Gruppen mit mindestens zwei Knoten werden zurückgegeben — ein einzelner Knoten braucht keinen
// Rahmen. Die funktionale Bezeichnung übernimmt den ersten nicht-leeren Wert der Gruppe.
export function deriveGroups(nodes: GraphNode[]): DerivedGroup[] {
  const order: string[] = [];
  const byId = new Map<string, DerivedGroup>();
  for (const node of nodes) {
    const g = groupOf(node);
    if (!g) continue;
    let group = byId.get(g.id);
    if (!group) {
      group = { id: g.id, label: g.label, function: g.function, nodeIds: [] };
      byId.set(g.id, group);
      order.push(g.id);
    }
    if (!group.function && g.function) group.function = g.function;
    group.nodeIds.push(node.id);
  }
  return order.map((id) => byId.get(id)!).filter((g) => g.nodeIds.length >= 2);
}

// Ein aus einer Gruppe berechneter Hintergrund-Rahmen (Position + Maße + Beschriftungen).
export interface GroupBox {
  id: string;
  label: string;
  function?: string;
  x: number;
  y: number;
  width: number;
  height: number;
}

// Berechnet aus den abgeleiteten Gruppen und den Knotenpositionen die umschließenden Rahmen.
// `positions` bildet Knoten-ID → {x,y} (linke obere Ecke der Knoten). `nodeW`/`nodeH` sind die
// angenommenen Knotenmaße, `pad` der Innenabstand des Rahmens; oben kommt zusätzlich Platz für das
// Label. Gruppen ohne bekannte Positionen werden übersprungen.
export function groupBoxes(
  groups: DerivedGroup[],
  positions: Map<string, { x: number; y: number }>,
  opts: { nodeW?: number; nodeH?: number; pad?: number; labelH?: number } = {},
): GroupBox[] {
  const nodeW = opts.nodeW ?? 176; // min-w-36 ≈ 144–176 px; großzügig
  const nodeH = opts.nodeH ?? 72;
  const pad = opts.pad ?? 24;
  const labelH = opts.labelH ?? 22;

  const boxes: GroupBox[] = [];
  for (const g of groups) {
    const pts = g.nodeIds
      .map((id) => positions.get(id))
      .filter((p): p is { x: number; y: number } => Boolean(p));
    if (pts.length === 0) continue;
    const minX = Math.min(...pts.map((p) => p.x));
    const minY = Math.min(...pts.map((p) => p.y));
    const maxX = Math.max(...pts.map((p) => p.x + nodeW));
    const maxY = Math.max(...pts.map((p) => p.y + nodeH));
    boxes.push({
      id: g.id,
      label: g.label,
      function: g.function,
      x: minX - pad,
      y: minY - pad - labelH,
      width: maxX - minX + pad * 2,
      height: maxY - minY + pad * 2 + labelH,
    });
  }
  return boxes;
}
